import { CitationMetadataFile, CitationMetadata } from '../types/citation';

// Load citation metadata from the static file
let citationMetadataCache: CitationMetadataFile | null = null;

export async function loadCitationMetadata(): Promise<CitationMetadataFile | null> {
  if (citationMetadataCache) {
    return citationMetadataCache;
  }

  try {
    // In production, this would be fetched from the backend
    // For now, we'll assume it's available as a static file
    const response = await fetch('/citation_metadata.json');
    if (response.ok) {
      citationMetadataCache = await response.json();
      return citationMetadataCache;
    }
  } catch (error) {
    console.warn('Could not load citation metadata:', error);
  }
  
  return null;
}

// Helper function to find metadata for a specific column
export function findColumnMetadata(
  metadataFile: CitationMetadataFile, 
  columnName: string
): { metadata: CitationMetadata; tableName: string } | null {
  for (const [tableName, tableInfo] of Object.entries(metadataFile.tables)) {
    if (tableInfo.columns[columnName]) {
      return {
        metadata: tableInfo.columns[columnName],
        tableName
      };
    }
  }
  return null;
}

// Helper function to format metadata keys for display
export function formatMetadataKey(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper function to format metadata into a readable structure
export function formatMetadataForDisplay(metadata: CitationMetadata, tableName?: string): Array<{ key: string; value: string }> {
  const formatted: Array<{ key: string; value: string }> = [];
  
  if (tableName) {
    formatted.push({ key: 'Table', value: tableName });
  }
  
  if (metadata.source_table) {
    formatted.push({ key: 'Source Table', value: metadata.source_table });
  }
  
  if (metadata.source_field) {
    formatted.push({ key: 'Source Field', value: metadata.source_field });
  }
  
  if (metadata.description) {
    formatted.push({ key: 'Description', value: metadata.description });
  }
  
  if (metadata.base_transformation) {
    formatted.push({ key: 'Transformation', value: metadata.base_transformation });
  }
  
  if (metadata.business_logic) {
    formatted.push({ key: 'Business Logic', value: metadata.business_logic });
  }
  
  if (metadata.join_logic) {
    formatted.push({ key: 'Join Logic', value: metadata.join_logic });
  }
  
  // Handle any additional fields
  Object.entries(metadata).forEach(([key, value]) => {
    if (value && typeof value === 'string' && 
        !['source_table', 'source_field', 'description', 'base_transformation', 'business_logic', 'join_logic'].includes(key)) {
      formatted.push({
        key: formatMetadataKey(key),
        value
      });
    }
  });
  
  return formatted;
}

// Helper function to get source metadata for columns used in a citation
export function getSourceMetadataForColumnsUsed(
  columnsUsed: string[],
  metadataFile: CitationMetadataFile
): Array<{ column: string; sourceTable: string; sourceField: string }> {
  const sources: Array<{ column: string; sourceTable: string; sourceField: string }> = [];
  
  for (const columnName of columnsUsed) {
    const metadata = findColumnMetadata(metadataFile, columnName);
    if (metadata) {
      sources.push({
        column: columnName,
        sourceTable: metadata.metadata.source_table || metadata.tableName,
        sourceField: metadata.metadata.source_field || columnName
      });
    }
  }
  
  return sources;
}
